import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import TextLink from 'LuxKit/TextLink'
import AccommodationRoomTileDetailsVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/AccommodationRoomTileDetailsVariant'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BedbankRoomCheapestOptionRateMobile from './BedbankRoomCheapestOptionRateMobile'
import BedbankRoomDetailsModal from './Modals/BedbankRoomDetailsModal'
import { useMultiRoomBookingGuard } from 'businessTraveller/hooks/useMultiRoomBookingGuard'

const DetailsContainer = styled.div`
  position: relative;
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

const StyledTextButton = styled(TextButton)`
  display: flex;
  flex: 1;
`

interface Props {
  offer: App.BedbankOffer;
  checkInDate?: string;
  checkOutDate?: string;
  packageView: App.BedbankOfferPackageView;
  fetchingRates?: boolean;
  isFilterActive: boolean;
  isHighlighted?: boolean;
  onViewPackageOptions?: () => void;
  detailsModalMode: 'room-and-package' | 'room-only';
}

function BedbankRoomOptionMobile(props: Props) {
  const {
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    fetchingRates,
    isFilterActive,
    isHighlighted = false,
    onViewPackageOptions,
    detailsModalMode,
  } = props

  const showModal = useContext(ModalContext)
  const rates = packageView.rates
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])

  const cheapestRate = useMemo(() => packageView.cheapestRate || (rates.length > 0 ? rates.sort((a, b) => a.totals.total - b.totals.total)[0] : null), [rates, packageView.cheapestRate])

  const hasMultipleCancellationPolicies = useMemo(() => {
    if (rates.every(rate => rate.refundable)) return false
    if (rates.every(rate => rate.partiallyRefundable)) return false
    if (rates.every(rate => !rate.refundable && !rate.partiallyRefundable)) return false
    return true
  }, [rates])
  const showPartialRefundPolicyLabel = useMemo(() => hasMultipleCancellationPolicies && !rates.some(rate => rate.refundable) && rates.some(rate => rate.partiallyRefundable), [hasMultipleCancellationPolicies, rates])

  const canSelectRoom = packageView.available && packageView.capacityValid && packageView.show
  const hasPromotions = offer.promotions?.length > 0

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions?.()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const validCheckinCheckout = !!(checkInDate && checkOutDate)
  const canPurchase = rates.length > 0 && packageView.capacityValid && packageView.available && !fetchingRates

  const roomTileActionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (fetchingRates) {
      return 'loading'
    }
    if (!canPurchase) {
      if (validCheckinCheckout && packageView.capacityValid) {
        return 'sold-out-on-dates'
      }
      if (packageView.available && !packageView.capacityValid) {
        return 'exceeded-capacity'
      }
    }
    if (validCheckinCheckout && !packageView.available && packageView.capacityValid) {
      return 'sold-out'
    }
    if (!isFilterActive && rates.length == 0 && checkInDate && checkOutDate) {
      return 'sold-out'
    }
    if (packageView.available && !packageView.capacityValid) {
      return 'exceeded-capacity'
    }
    if (packageView.available && packageView.capacityValid && !validCheckinCheckout) {
      return 'available-on-dates'
    }
  }, [canPurchase, checkInDate, checkOutDate, fetchingRates, packageView.available, isFilterActive, packageView.capacityValid, rates.length, validCheckinCheckout])

  const offerPageDispatch = useOfferPageDispatch()

  const handleAddAnotherUnitClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const guardBookingFlow = useMultiRoomBookingGuard()

  const handleCheckAvailabilityClick = useCallback(() => {
    if (guardBookingFlow()) return
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [guardBookingFlow, offerPageDispatch, packageView.package.roomId])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, packageView.package.roomId])

  const onShowRoomDetails = useCallback(() => {
    showModal(<BedbankRoomDetailsModal roomPackage={packageView.package} />)
  }, [packageView, showModal])

  const onShowRoomDetailsAndMaybePackage = useCallback(() => {
    if (detailsModalMode === 'room-and-package') {
      offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else {
      showModal(<BedbankRoomDetailsModal roomPackage={packageView.package} />)
    }
  }, [detailsModalMode, offerPageDispatch, packageView, showModal])

  return <Pane border="neutral-five" borderRadius="S" data-testid="PackageOption-bedbank">
    {isHighlighted && <MessageBanner
      kind="promotional-primary"
      description={<b>You were interested in this option</b>}
      icon={<LineSightseeingIcon />}
    />}
    <ScrollAnchor id={packageView.package.roomId} vOffset={rem(80)} />
    <AccommodationRoomTile roomPackage={packageView.package}>
      <AccommodationRoomTileImage roomPackage={packageView.package} />
      <AccommodationRoomTileDetailsVariant
        roomPackage={packageView.package}
        urgencyLabels={<>
          {hasPromotions && <BookingsCountLabel
            bookings={packageView.package.stats.booked}
            threshold="room"
          />}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        actions={<CSSBreakpoint min="desktop">
          <TripGuard>
            <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
          </TripGuard>
        </CSSBreakpoint>}
      >
        <AccommodationRoomTileActions
          roomPackage={packageView.package}
          state={roomTileActionsState}
          onAddAnotherUnitClick={handleAddAnotherUnitClick}
          onChangeDatesClick={handleChangeDatesClick}
          onCheckAvailabilityClick={handleCheckAvailabilityClick}
        />
      </AccommodationRoomTileDetailsVariant>
    </AccommodationRoomTile>
    <DetailsContainer>
      <LoadingIndicator visible={fetchingRates} floating opaque />
      {!fetchingRates && !validCheckinCheckout && !rates.length && (
        <Group direction="horizontal" gap={8}>
          <StyledTextButton size="large" kind="secondary" onClick={onShowRoomDetails}>View details</StyledTextButton>
          <StyledTextButton size="large" kind="primary" onClick={handleCheckAvailabilityClick}>Check availability</StyledTextButton>
        </Group>
      )}
      {!fetchingRates && validCheckinCheckout && !canSelectRoom && (
        <Group direction="vertical" gap={8}>
          {!packageView.capacityValid && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room cannot accomodate your number of guests"
            description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>add more rooms</TextLink></>}
          />}
          {(!packageView.available || !packageView.show) && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room is sold out on your dates or max number of guests exceeded"
            description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink></>}
          />}
          <TextButton kind="secondary" size="large" onClick={onShowRoomDetails}>
            View details
          </TextButton>
        </Group>
      )}
      {!fetchingRates && cheapestRate && canSelectRoom && <BedbankRoomCheapestOptionRateMobile
        rate={cheapestRate}
        offer={offer}
        packageView={packageView}
        hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
        showPartialRefundPolicyLabel={showPartialRefundPolicyLabel}
        onShowRoomDetails={onShowRoomDetailsAndMaybePackage}
      />}
    </DetailsContainer>
  </Pane>
}

type MappedProps = Pick<Props, 'isFilterActive' | 'checkInDate' | 'checkOutDate'>

export default withOfferPageAndReduxState((offerState): MappedProps => ({
  isFilterActive: !!offerState.activeFilter,
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
}), BedbankRoomOptionMobile)
